import Papa from 'papaparse'
import {
  MAX_METADATA_CSV_EXPORT_TRACK_COUNT,
  METADATA_FIELDS_KEYS,
} from '../constants/TrackMetadata'
import {
  pushUINotification,
  dismissUINotification,
} from '@disco/utils/notifications'
import { Track, TrackId } from 'types/track'
import { Contact } from 'types/list'
import { Pro, Publisher } from 'types/writer'
import { Playlist } from 'types/playlist'
import type { RightsSplitChain } from 'types/rights-data'

interface ExportActions {
  getPlaylistTracks?: (playlist: Playlist) => Promise<any>
  fetchTrackMetadata: (trackId: TrackId) => Promise<Track | undefined>
  fetchTrackRightsSplits: (trackId: TrackId) => Promise<RightsSplitChain[]>
}

interface ExportData {
  tracks: Record<TrackId, Track>
  contacts: Contact[]
  pros: Pro[]
  publishers: Publisher[]
  customFieldsKeys: string[]
}

export async function generateMetadataCsv({
  playlist,
  manualTracks,
  data,
  actions,
}: {
  playlist?: Playlist
  manualTracks?: Track[]
  data: ExportData
  actions: ExportActions
}) {
  if (!playlist && !manualTracks) return

  const notificationUid = `csv-generation-${playlist ? playlist.id : 'manual'}`
  const name = playlist ? playlist.name : 'metadata-export'

  const trackCount = playlist ? playlist.track_count : manualTracks?.length ?? 0
  if (trackCount > MAX_METADATA_CSV_EXPORT_TRACK_COUNT) {
    pushUINotification({
      uid: notificationUid,
      _replaceNotificationWithUID: notificationUid,
      level: 'error',
      title: 'Too many tracks to export',
      message: 'Please contact the Support team for assistance.',
      dismissible: 'button',
    })
    return
  }

  pushUINotification({
    uid: notificationUid,
    _replaceNotificationWithUID: notificationUid,
    level: 'success',
    title: 'Generating CSV...',
    message: `The metadata CSV for ‘${name}’ is being generated and will download shortly.`,
    dismissible: 'button',
  })

  try {
    let trackIds: TrackId[] = []

    if (playlist) {
      if (!playlist.tracks || playlist.tracks.length === 0) {
        if (actions.getPlaylistTracks) {
          const result = await actions.getPlaylistTracks(playlist)
          trackIds = (result?.tracks ?? []).map((t: any) =>
            typeof t === 'object' ? t.id : t
          )
        }
      } else {
        trackIds = (playlist.tracks as any as TrackId[]) || []
      }
    } else if (manualTracks) {
      trackIds = manualTracks.map(t => t.id)
    }

    // Identify tracks needing detailed metadata
    const tracksToFetch = trackIds.filter(id => {
      const track = data.tracks[id]
      return !track || track.native_tags == undefined
    })

    const returnedTracks: Record<TrackId, Track> = { ...data.tracks }
    if (tracksToFetch.length > 0) {
      const fetched = await Promise.all(tracksToFetch.map(id => actions.fetchTrackMetadata(id)))

      tracksToFetch.forEach((id, index) => {
        if (fetched[index]) returnedTracks[id] = fetched[index]
      })
    }

    const rightsSplitsMap: Record<TrackId, RightsSplitChain[]> = {}
    const splitResults = await Promise.all(
      trackIds.map(id => actions.fetchTrackRightsSplits(id).catch((): RightsSplitChain[] => []))
    )
    trackIds.forEach((id, index) => {
      rightsSplitsMap[id] = splitResults[index] ?? []
    })

    const exportRows = trackIds
      .map((trackId, index) => {
        const track = returnedTracks[trackId]
        if (!track) return null
        const row: Record<string, any> = {
          'Playlist Position': index + 1,
          'Track ID': track.id,
          Title: track.name,
        }

        METADATA_FIELDS_KEYS.forEach(key => {
          if (key === 'Title') return
          row[key] = track.metadata[key] || ''
        })

        row['External ID'] = track.metadata['External Id'] || ''

        row.Notes =
          track.comments && track.comments.length > 0
            ? track.comments.map(c => c.comment).join('; ')
            : ''

        const publishingChains = (rightsSplitsMap[trackId] || []).filter(
          chain => chain.rights_type === 'publishing'
        )

        for (let i = 0; i < 4; i++) {
          const slotIndex = i + 1
          const chain = publishingChains[i]
          const writerMember = chain?.members.find(m => m.entity.entity_type === 'Writer')
          const proMember = chain?.members.find(m => m.entity.entity_type === 'PRO')
          const publisherMember = chain?.members.find(m => m.entity.entity_type === 'Publisher')
          row[`Writer ${slotIndex} Name`] = writerMember?.entity.title || ''
          row[`Writer ${slotIndex} Percentage`] = chain?.rights_percentage != null
            ? `${(chain.rights_percentage / 100).toFixed(2)}%`
            : ''
          row[`Writer ${slotIndex} PRO`] = proMember?.entity.title || ''
          row[`Writer ${slotIndex} Publisher`] = publisherMember?.entity.title || ''
        }

        data.customFieldsKeys.forEach(key => {
          row[key] = track.metadata[key] || ''
        })

        row['Native Tags'] =
          track.native_tags && track.native_tags.length > 0
            ? track.native_tags.map(t => t.name).join('; ')
            : ''
        return row
      })
      .filter(Boolean)

    const allKeys = new Set<string>()
    exportRows.forEach(row => {
      if (!row) return
      const rowObj = row as Record<string, any>
      Object.keys(rowObj).forEach(k => allKeys.add(k))
    })

    const priority = [
      'Playlist Position',
      'Track ID',
      'Title',
      'External ID',
      'Notes',
    ]
    const sortedKeys = Array.from(allKeys).sort((a, b) => {
      const idxA = priority.indexOf(a)
      const idxB = priority.indexOf(b)
      if (idxA !== -1 && idxB !== -1) return idxA - idxB
      if (idxA !== -1) return -1
      if (idxB !== -1) return 1
      return a.localeCompare(b)
    })

    if (sortedKeys.length === 0) {
      dismissUINotification(notificationUid)
      return
    }

    const csv = Papa.unparse(exportRows, { columns: sortedKeys })
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url

    const safeName = (name || 'export').replace(/[/\\?%*:|"<>]/g, '-')
    link.setAttribute('download', `${safeName}.csv`)

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)

    dismissUINotification(notificationUid)
  } catch (error) {
    console.error('Failed to generate metadata CSV:', error)
    pushUINotification({
      uid: notificationUid,
      _replaceNotificationWithUID: notificationUid,
      level: 'error',
      title: 'CSV export failed',
      message: 'Please contact the Support team for assistance.',
      dismissible: 'button',
    })
  }
}
